import { memo } from 'react';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import { WaitNodeData } from '../../../mappers/map-canvas-content.types';

export const WaitNode = memo(({ data, selected }: NodeProps<Node<WaitNodeData, 'wait'>>) => {
  const pct = data?.percentage;
  const headerHelperText =
    'Delay workflow execution until a specified percentage of time remains between shift creation and start time. For example, with 90% and a shift created at 10:20 starting at 10:30, execution continues at 10:21 when 90% of the time remains.';

  return (
    <Tooltip placement="top" title={headerHelperText}>
      <NodeWrapper className="nopan" selected={selected}>
        <HeaderWrapper>
          <TitleWrapper>
            <Typography variant="caption" fontWeight={700}>
              Timed Delay
            </Typography>
          </TitleWrapper>
        </HeaderWrapper>
        <Value>
          {pct === undefined || pct === null ? (
            'Not configured'
          ) : (
            <>
              Execution delayed until <Highlight>{pct}%</Highlight> of time remains between shift
              creation and start time.
            </>
          )}
        </Value>

        <Handle type="target" position={Position.Left} isConnectable={false} />
        <Handle type="source" position={Position.Right} isConnectable={false} />
      </NodeWrapper>
    </Tooltip>
  );
});

const NodeWrapper = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  position: 'relative',
  backgroundColor: theme.palette.lwWhite[100],
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.lwSecondaryAccentLight[20]}`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(2),
  width: 250,
  fontWeight: 700,
  textAlign: 'center',
  boxShadow: selected ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
}));

const Value = styled(Box)({
  fontWeight: 500,
  fontSize: '10px',
  wordBreak: 'break-word',
  textAlign: 'justify',
});

const Highlight = styled('span')(({ theme }) => ({
  fontWeight: 800,
  color: theme.palette.lwBlack[100],
  fontSize: theme.spacing(3),
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(2),
}));

const HeaderWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
});

const TitleWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});
